import { useEffect, useRef } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Check, Circle, LoaderCircle, RotateCw, Trash2, X } from 'lucide-react'
import type { ClientsAdminApi } from '../adminApi.js'
import type { AdminClientSummary, ClientPurge } from '../index.js'
import { Modal } from '../../core/index.js'
import { Button } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi

/** The kinds of record someone would recognise from setting a client up, by the key the API counts them under. */
export const DELETED_LABELS: [key: string, singular: string, plural: string][] = [
  ['facilities', 'facility', 'facilities'],
  ['workers', 'worker', 'workers'],
  ['shifts', 'shift', 'shifts'],
  ['shiftOutcomes', 'shift outcome', 'shift outcomes'],
  ['behaviorEvents', 'custom behavior event', 'custom behavior events'],
  ['rankingDecisions', 'ranking', 'rankings'],
  ['operators', 'operator', 'operators'],
  ['facilityAdministrators', 'facility administrator', 'facility administrators'],
]

/** What the dialog lists, in the order a demo was built: the named kinds, then everything kept per worker and per facility, then the client itself. */
const STEPS: { id: string; label: string; keys: readonly string[] | 'rest' | 'registration' }[] = [
  { id: 'facilities', label: 'Facilities and their configuration', keys: ['facilities'] },
  { id: 'workers', label: 'Workers', keys: ['workers'] },
  { id: 'shifts', label: 'Open shifts', keys: ['shifts'] },
  { id: 'shiftOutcomes', label: 'Shift outcomes', keys: ['shiftOutcomes'] },
  { id: 'behaviorEvents', label: 'Custom behavior events', keys: ['behaviorEvents'] },
  { id: 'rankingDecisions', label: 'Rankings', keys: ['rankingDecisions'] },
  { id: 'facilityAdministrators', label: 'Facility administrators', keys: ['facilityAdministrators'] },
  { id: 'operators', label: 'Operator logins', keys: ['operators'] },
  { id: 'rest', label: 'Scores, messages, worker logins and other records', keys: 'rest' },
  { id: 'registration', label: 'The client, its API key and scopes', keys: 'registration' },
]
const NAMED_KEYS = new Set(STEPS.flatMap((step) => (Array.isArray(step.keys) ? step.keys : [])))

/** How often the record of a running delete is read. */
const FOLLOW_EVERY_MS = 1500

type Step = (typeof STEPS)[number]
type StepState = 'waiting' | 'running' | 'done' | 'failed'

/** The kinds of record a step covers: its own keys, or every kind the delete names that no other step does. */
function keysOf(step: Step, purge: ClientPurge): readonly string[] {
  if (step.keys === 'rest') {
    return purge.stores.filter((key) => !NAMED_KEYS.has(key))
  }
  return step.keys === 'registration' ? [] : step.keys
}

/** Whether this step's records have all been cleared - the registration's only once the whole delete is done. */
function isStepDone(step: Step, purge: ClientPurge): boolean {
  if (step.keys === 'registration') {
    return purge.status === 'done'
  }
  return keysOf(step, purge).every((key) => key in purge.deleted)
}

function stepState(step: Step, purge: ClientPurge | null, failedToStart: boolean): StepState {
  if (failedToStart) {
    return 'failed'
  }
  if (!purge) {
    return 'waiting'
  }
  if (isStepDone(step, purge)) {
    return 'done'
  }
  return purge.status === 'failed' ? 'failed' : 'running'
}

function countFor(step: Step, purge: ClientPurge): number | null {
  if (step.keys === 'registration') {
    return null
  }
  return keysOf(step, purge).reduce((sum, key) => sum + (purge.deleted[key] ?? 0), 0)
}

const MARKERS: Record<StepState, { Icon: typeof Check; tone: string }> = {
  waiting: { Icon: Circle, tone: 'text-ink-mid' },
  running: { Icon: LoaderCircle, tone: 'text-marine' },
  done: { Icon: Check, tone: 'text-green' },
  failed: { Icon: X, tone: 'text-coral' },
}

/** The trash button on a demo client's row. It only opens the dialog; nothing is deleted from here. */
export function DeleteDemoClientButton({ client, onClick }: { client: AdminClientSummary; onClick: () => void }) {
  return (
    <button
      type="button"
      title={`Delete demo client ${client.organizationName}`}
      aria-label={`Delete demo client ${client.organizationName}`}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
      className="inline-flex items-center rounded border border-coral/50 px-1.5 py-1 text-coral hover:bg-coral-lt"
    >
      <Trash2 aria-hidden="true" strokeWidth={2} className="h-3.5 w-3.5" />
    </button>
  )
}

interface DeleteDemoClientModalProps {
  api: AdminApi
  client: AdminClientSummary
  clientId: string
  /** Told as soon as the delete finishes, so the list behind refreshes while this stays open on what was removed. */
  onDeleted: (result: ClientPurge) => void
  onClose: () => void
}

/**
 * Deleting a demo client, in the same shape as building one: the list of
 * what goes, each line marked as it stands - waiting, in flight, done with
 * its count, or failed. The delete runs in the background on the API (a
 * large demo takes longer than one request may), so the dialog starts it
 * and then follows its record every second or so, each line going green
 * as its records are cleared. Irreversible, so nothing happens until
 * Delete permanently is pressed. Only shown for a demo client, and the API
 * refuses any other. Closing the dialog mid-way stops nothing: the delete
 * carries on, and opening it again picks the same one up.
 */
export function DeleteDemoClientModal({ api, client, clientId, onDeleted, onClose }: DeleteDemoClientModalProps) {
  const start = useMutation({ mutationFn: () => api.deleteDemoClient(clientId) })
  const started = start.data ?? null
  const followed = useQuery({
    queryKey: ['admin', 'client-purge', clientId, started?.startedAt ?? null],
    queryFn: () => api.clientPurge(clientId),
    enabled: started !== null,
    // Keep reading while it runs; a read that fails on the way (a blip, a cold start) is simply tried again.
    refetchInterval: (query) => (query.state.data && query.state.data.status !== 'running' ? false : FOLLOW_EVERY_MS),
    retry: true,
    retryDelay: FOLLOW_EVERY_MS,
  })
  const purge = followed.data ?? started
  const failedToStart = start.isError
  const isRunning = start.isPending || purge?.status === 'running'
  const isDone = purge?.status === 'done'
  const isFailed = failedToStart || purge?.status === 'failed'
  const failure = failedToStart ? start.error.message : purge?.error ?? null

  // The list behind is told once per finished delete, not once per read of its record.
  const reported = useRef<string | null>(null)
  useEffect(() => {
    if (purge?.status === 'done' && reported.current !== purge.startedAt) {
      reported.current = purge.startedAt
      onDeleted(purge)
    }
  }, [purge, onDeleted])

  const title = isDone ? `Deleted ${client.organizationName}` : isRunning ? `Deleting ${client.organizationName}…` : `Delete ${client.organizationName}`
  const subtitle = isDone
    ? 'Everything below was removed.'
    : isRunning
      ? 'Running in the background. You can close this and come back; the delete carries on.'
      : 'This permanently removes the client and everything under it. It cannot be undone.'

  return (
    <Modal
      eyebrow="Demo client"
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-4">
          {isDone ? (
            <Button icon={Check} onClick={onClose} autoFocus>
              Close
            </Button>
          ) : (
            <>
              <Button icon={isFailed && !isRunning ? RotateCw : Trash2} onClick={() => start.mutate()} disabled={isRunning}>
                {isRunning ? 'Deleting…' : isFailed ? 'Try again' : 'Delete permanently'}
              </Button>
              <button type="button" onClick={onClose} className="text-xs font-bold text-ink-mid hover:text-ink">
                {isRunning ? 'Close and let it run' : 'Cancel'}
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="px-6 py-5">
        <ol className="space-y-1.5">
          {STEPS.map((step) => {
            const state = stepState(step, purge, failedToStart)
            const { Icon, tone } = MARKERS[state]
            const count = purge && state === 'done' ? countFor(step, purge) : null
            return (
              <li key={step.id} className={`text-sm flex gap-2 ${tone}`}>
                <Icon aria-hidden="true" strokeWidth={2.5} className={`mt-0.5 h-4 w-4 shrink-0 ${state === 'running' ? 'animate-spin' : ''} ${state === 'waiting' ? 'opacity-40' : ''}`} />
                <span className={state === 'waiting' ? '' : 'font-bold'}>
                  {step.label}
                  {count !== null && <span className="font-normal text-ink-mid"> · {count === 0 ? 'none' : `${count.toLocaleString()} removed`}</span>}
                </span>
              </li>
            )
          })}
        </ol>
        {isFailed && (
          <div className="border border-coral bg-coral-lt p-4 mt-5 text-sm text-ink">
            <div className="font-bold text-coral mb-1">Deleting stopped partway</div>
            <p>{failure ?? 'The delete did not finish'}. Nothing is lost by trying again: anything already removed stays removed, and the client is still listed until the last step.</p>
          </div>
        )}
        {isDone && <div className="text-sm font-bold text-green mt-5">Demo client deleted.</div>}
      </div>
    </Modal>
  )
}
